import { createFileRoute } from "@tanstack/react-router";
import { L, LegalAside, LegalPage, LegalSection } from "@/components/LegalPage";

export const Route = createFileRoute("/refunds")({
  head: () => {
    const title = "Refunds — WHO KNEW";
    const description =
      "How refunds work at WHO KNEW. 14 days from purchase, no questions asked.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/refunds" },
      ],
    };
  },
  component: RefundsPage,
});

function RefundsPage() {
  return (
    <LegalPage eyebrow="REFUNDS" title="How refunds work.">
      <LegalSection label="THE SHORT VERSION" first>
        <L>
          You have 14 days from the date of purchase to ask for a refund. No questions
          asked. Email hello@whoknew.guide with the email address you used to purchase, and
          we'll process it within five working days.
        </L>
      </LegalSection>

      <LegalSection label="THE FULL VERSION">
        <L>
          We want you to actually use what you've bought. If a <span className="text-burgundy">WHO KNEW</span> product isn't
          for you, take your money back. We'd rather have a small number of people who
          got value from it than a larger number who regretted the purchase.
        </L>
        <L>
          The 14-day window starts the day you complete payment. It ends 14 days later
          at 23:59 GMT.
        </L>
      </LegalSection>

      <LegalSection label="HOW TO ASK FOR A REFUND">
        <ol style={{ marginTop: "16px", paddingLeft: "20px", listStyleType: "decimal" }}>
          <li>Email hello@whoknew.guide from the address you used to purchase.</li>
          <li style={{ marginTop: "8px" }}>
            Include the word "refund" in the subject line, so we spot it quickly.
          </li>
          <li style={{ marginTop: "8px" }}>
            Tell us anything you'd like us to know — what didn't work, what you expected,
            what we could do better. You don't have to. But it helps us improve.
          </li>
        </ol>
        <L>
          You'll get your money back within five working days, to the card or payment
          method you originally used.
        </L>
      </LegalSection>

      <LegalSection label="AFTER 14 DAYS">
        <L>
          After 14 days, refunds aren't guaranteed. But if something genuinely went wrong
          — you couldn't access what you bought, the materials were unusable, life
          events derailed you — write to us. We'll look at it on the merits.
        </L>
      </LegalSection>

      <LegalSection label="WHAT YOU KEEP">
        <L>
          If you refund, we close your access to the product. You're welcome to keep any
          workbooks, templates, or assets you've already downloaded — we don't track
          those, and we'd feel petty asking for them back.
        </L>
        <LegalAside>
          A refund isn't a personal failure. Sometimes a thing isn't the right thing. We
          don't take it personally and you shouldn't either.
        </LegalAside>
      </LegalSection>
    </LegalPage>
  );
}
